import React from 'react';
import '../scss/components/_tenantnoticemodal.scss';

const OwnerTenantNoticeModal = ({ isOpen, onClose, noticeData, loading }) => {
  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // ✅ Calculate remaining days from TODAY to vacate date (dynamic - decreases daily)
  const calculateRemainingDays = (vacateDate) => {
    if (!vacateDate) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const vacate = new Date(vacateDate);
    vacate.setHours(0, 0, 0, 0);
    
    const diffTime = vacate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // ✅ Calculate total notice period from notice date to vacate date (fixed)
  const calculateTotalNoticePeriod = (noticeDate, vacateDate) => {
    if (!noticeDate || !vacateDate) return null;
    
    const notice = new Date(noticeDate);
    notice.setHours(0, 0, 0, 0);
    
    const vacate = new Date(vacateDate);
    vacate.setHours(0, 0, 0, 0);
    
    const diffTime = vacate.getTime() - notice.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // ✅ Get notice type based on total notice period (fixed - doesn't change daily)
  const getNoticeTypeInfo = (totalDays) => {
    if (totalDays === null || totalDays === undefined) {
      return {
        label: 'Standard Notice',
        color: '#F59E0B',
        bg: '#FFFBEB',
        border: '#FDE68A',
        textColor: '#92400E'
      };
    }
    
    // If total notice period is less than 30 days, it's a short notice
    if (totalDays < 30) {
      return {
        label: `Short Notice (${totalDays} days)`,
        color: '#F59E0A',
        bg: '#FFFBEA',
        border: '#FDE68B',
        textColor: '#92400F'
      };
    }
    
    // 30 days or more is standard notice
    return {
      label: `Standard Notice (${totalDays} days)`,
      color: '#F59E0B',
      bg: '#FFFBEB',
      border: '#FDE68A',
      textColor: '#92400E'
    };
  };

  // Calculate both values
  const totalNoticeDays = calculateTotalNoticePeriod(noticeData?.NoticeDate, noticeData?.VacateDate);
  const remainingDays = calculateRemainingDays(noticeData?.VacateDate);
  const noticeTypeInfo = getNoticeTypeInfo(totalNoticeDays);

  return (
    <div className="tnm-overlay">
      <div className="tnm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tnm-header">
          <div className="tnm-header-left">
            <h3>Notice Period Details</h3>
            {noticeData && totalNoticeDays !== null && (
              <span 
                className="tnm-notice-badge"
                style={{
                  background: noticeTypeInfo.bg,
                  color: noticeTypeInfo.textColor || noticeTypeInfo.color,
                  borderColor: noticeTypeInfo.border
                }}
              >
                {noticeTypeInfo.label}
              </span>
            )}
          </div>
          <button className="tnm-close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="tnm-body">
          {loading ? (
            <div className="tnm-loading">Loading notice details...</div>
          ) : noticeData ? (
            <div className="tnm-content">
              <div className="tnm-row">
                <span className="tnm-label">Tenant Name:</span>
                <span className="tnm-value">{noticeData.TenantName || 'N/A'}</span>
              </div>
              
              <div className="tnm-row">
                <span className="tnm-label">Room Number:</span>
                <span className="tnm-value">{noticeData.RoomNumber || 'N/A'}</span>
              </div>
              
              <div className="tnm-row">
                <span className="tnm-label">Bed Number:</span>
                <span className="tnm-value">{noticeData.BedNumber || 'N/A'}</span>
              </div>
              
              <div className="tnm-row">
                <span className="tnm-label">PG Name:</span>
                <span className="tnm-value">{noticeData.PGName || 'N/A'}</span>
              </div>
              
              <div className="tnm-row">
                <span className="tnm-label">Notice Date:</span>
                <span className="tnm-value">{formatDate(noticeData.NoticeDate)}</span>
              </div>
              
              <div className="tnm-row">
                <span className="tnm-label">Vacate Date:</span>
                <span className="tnm-value">{formatDate(noticeData.VacateDate)}</span>
              </div>

              {/* ✅ Days Remaining - Dynamic (decreases daily) */}
              {remainingDays !== null && (
                <div className="tnm-row tnm-period-row">
                  <span className="tnm-label">Days Remaining:</span>
                  <span 
                    className="tnm-value tnm-period-days"
                    style={{
                      color: remainingDays < 0 ? '#D97710' : remainingDays < 30 ? '#D97709' : '#D97706',
                      fontWeight: '600'
                    }}
                  >
                    {remainingDays < 0 
                      ? `Overdue by ${Math.abs(remainingDays)} days ⚠️`
                      : remainingDays === 0
                        ? 'Last day today! 🔥'
                        : `${remainingDays} days ${remainingDays < 30 ? '⚠️' : ''}`
                    }
                  </span>
                </div>
              )}
              
              <div className="tnm-row">
                <span className="tnm-label">Notice Status:</span>
                <span className={`tnm-status ${noticeData.NoticeStatus?.toLowerCase()}`}>
                  {noticeData.NoticeStatus || 'N/A'}
                </span>
              </div>
              
              <div className="tnm-row tnm-row-full">
                <span className="tnm-label">Notice Reason:</span>
                <span className="tnm-value">{noticeData.NoticeReason || 'No reason provided'}</span>
              </div>
              
              {noticeData.ApprovedBy && (
                <div className="tnm-row">
                  <span className="tnm-label">Approved By:</span>
                  <span className="tnm-value">{noticeData.ApprovedBy}</span>
                </div>
              )}
              
              {noticeData.ApprovedDate && (
                <div className="tnm-row">
                  <span className="tnm-label">Approved Date:</span>
                  <span className="tnm-value">{formatDate(noticeData.ApprovedDate)}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="tnm-error">No notice details found</div>
          )}
        </div>
        
        <div className="tnm-footer">
          <button className="tnm-close-footer-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OwnerTenantNoticeModal;

// import React from 'react';
// import '../scss/components/_tenantnoticemodal.scss';

// const OwnerTenantNoticeModal = ({ isOpen, onClose, noticeData, loading }) => {
//   if (!isOpen) return null;

//   const formatDate = (dateString) => {
//     if (!dateString) return 'N/A';
//     const date = new Date(dateString);
//     return date.toLocaleDateString('en-US', {
//       year: 'numeric',
//       month: 'short',
//       day: 'numeric'
//     });
//   };

//   // Calculate notice period days
//   const calculateNoticePeriod = (noticeDate, vacateDate) => {
//     if (!noticeDate || !vacateDate) return null;
    
//     const notice = new Date(noticeDate);
//     const vacate = new Date(vacateDate);
    
//     // Calculate difference in days
//     const diffTime = vacate.getTime() - notice.getTime();
//     const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
//     return diffDays;
//   };

//   // Get notice type and styles
//   const getNoticeTypeInfo = (diffDays) => {
//     if (diffDays === null || diffDays === undefined) {
//       return {
//         label: 'Standard Notice',
//         color: '#F59E0B',
//         bg: '#FFFBEB',
//         border: '#FDE68A'
//       };
//     }
    
//     // If less than 30 days, it's a short notice
//     if (diffDays < 30) {
//       return {
//         label: `Short Notice (${diffDays} days)`,
//         color: '#EF4444',
//         bg: '#FEF2F2',
//         border: '#FECACA',
//         textColor: '#991B1B'
//       };
//     }
    
//     // 30 days or more is standard notice
//     return {
//       label: `Standard Notice (${diffDays} days)`,
//       color: '#F59E0B',
//       bg: '#FFFBEB',
//       border: '#FDE68A',
//       textColor: '#92400E'
//     };
//   };

//   // Calculate notice period
//   const noticeDays = calculateNoticePeriod(noticeData?.NoticeDate, noticeData?.VacateDate);
//   const noticeTypeInfo = getNoticeTypeInfo(noticeDays);

//   return (
//     <div className="tnm-overlay">
//       <div className="tnm-modal" onClick={(e) => e.stopPropagation()}>
//         <div className="tnm-header">
//           <div className="tnm-header-left">
//             <h3>Notice Period Details</h3>
//             {noticeData && noticeDays !== null && (
//               <span 
//                 className="tnm-notice-badge"
//                 style={{
//                   background: noticeTypeInfo.bg,
//                   color: noticeTypeInfo.textColor || noticeTypeInfo.color,
//                   borderColor: noticeTypeInfo.border
//                 }}
//               >
//                 {noticeTypeInfo.label}
//               </span>
//             )}
//           </div>
//           <button className="tnm-close-btn" onClick={onClose}>×</button>
//         </div>
        
//         <div className="tnm-body">
//           {loading ? (
//             <div className="tnm-loading">Loading notice details...</div>
//           ) : noticeData ? (
//             <div className="tnm-content">
//               <div className="tnm-row">
//                 <span className="tnm-label">Tenant Name:</span>
//                 <span className="tnm-value">{noticeData.TenantName || 'N/A'}</span>
//               </div>
              
//               <div className="tnm-row">
//                 <span className="tnm-label">Room Number:</span>
//                 <span className="tnm-value">{noticeData.RoomNumber || 'N/A'}</span>
//               </div>
              
//               <div className="tnm-row">
//                 <span className="tnm-label">Bed Number:</span>
//                 <span className="tnm-value">{noticeData.BedNumber || 'N/A'}</span>
//               </div>
              
//               <div className="tnm-row">
//                 <span className="tnm-label">PG Name:</span>
//                 <span className="tnm-value">{noticeData.PGName || 'N/A'}</span>
//               </div>
              
//               <div className="tnm-row">
//                 <span className="tnm-label">Notice Date:</span>
//                 <span className="tnm-value">{formatDate(noticeData.NoticeDate)}</span>
//               </div>
              
//               <div className="tnm-row">
//                 <span className="tnm-label">Vacate Date:</span>
//                 <span className="tnm-value">{formatDate(noticeData.VacateDate)}</span>
//               </div>

//               {/* Add Notice Period Days row */}
//               {noticeDays !== null && (
//                 <div className="tnm-row tnm-period-row">
//                   <span className="tnm-label">Notice Period:</span>
//                   <span 
//                     className="tnm-value tnm-period-days"
//                     style={{
//                       color: noticeDays < 30 ? '#DC2626' : '#D97706',
//                       fontWeight: '600'
//                     }}
//                   >
//                     {noticeDays} days {noticeDays < 30 && '⚠️'}
//                   </span>
//                 </div>
//               )}
              
//               <div className="tnm-row">
//                 <span className="tnm-label">Notice Status:</span>
//                 <span className={`tnm-status ${noticeData.NoticeStatus?.toLowerCase()}`}>
//                   {noticeData.NoticeStatus || 'N/A'}
//                 </span>
//               </div>
              
//               <div className="tnm-row tnm-row-full">
//                 <span className="tnm-label">Notice Reason:</span>
//                 <span className="tnm-value">{noticeData.NoticeReason || 'No reason provided'}</span>
//               </div>
              
//               {noticeData.ApprovedBy && (
//                 <div className="tnm-row">
//                   <span className="tnm-label">Approved By:</span>
//                   <span className="tnm-value">{noticeData.ApprovedBy}</span>
//                 </div>
//               )}
              
//               {noticeData.ApprovedDate && (
//                 <div className="tnm-row">
//                   <span className="tnm-label">Approved Date:</span>
//                   <span className="tnm-value">{formatDate(noticeData.ApprovedDate)}</span>
//                 </div>
//               )}
//             </div>
//           ) : (
//             <div className="tnm-error">No notice details found</div>
//           )}
//         </div>
        
//         <div className="tnm-footer">
//           <button className="tnm-close-footer-btn" onClick={onClose}>
//             Close
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default OwnerTenantNoticeModal;

// import React from 'react';
// import '../scss/components/_tenantnoticemodal.scss'; // You'll need to create this

// const OwnerTenantNoticeModal = ({ isOpen, onClose, noticeData, loading }) => {
//   if (!isOpen) return null;

//   const formatDate = (dateString) => {
//     if (!dateString) return 'N/A';
//     const date = new Date(dateString);
//     return date.toLocaleDateString('en-US', {
//       year: 'numeric',
//       month: 'short',
//       day: 'numeric'
//     });
//   };

//   return (
//     <div className="tnm-overlay" >
//       <div className="tnm-modal" onClick={(e) => e.stopPropagation()}>
//         <div className="tnm-header">
//           <h3>Notice Period Details</h3>
//           <button className="tnm-close-btn" onClick={onClose}>×</button>
//         </div>
        
//         <div className="tnm-body">
//           {loading ? (
//             <div className="tnm-loading">Loading notice details...</div>
//           ) : noticeData ? (
//             <div className="tnm-content">
//               <div className="tnm-row">
//                 <span className="tnm-label">Tenant Name:</span>
//                 <span className="tnm-value">{noticeData.TenantName || 'N/A'}</span>
//               </div>
              
//               <div className="tnm-row">
//                 <span className="tnm-label">Room Number:</span>
//                 <span className="tnm-value">{noticeData.RoomNumber || 'N/A'}</span>
//               </div>
              
//               <div className="tnm-row">
//                 <span className="tnm-label">Bed Number:</span>
//                 <span className="tnm-value">{noticeData.BedNumber || 'N/A'}</span>
//               </div>
              
//               <div className="tnm-row">
//                 <span className="tnm-label">PG Name:</span>
//                 <span className="tnm-value">{noticeData.PGName || 'N/A'}</span>
//               </div>
              
//               <div className="tnm-row">
//                 <span className="tnm-label">Notice Date:</span>
//                 <span className="tnm-value">{formatDate(noticeData.NoticeDate)}</span>
//               </div>
              
//               <div className="tnm-row">
//                 <span className="tnm-label">Vacate Date:</span>
//                 <span className="tnm-value">{formatDate(noticeData.VacateDate)}</span>
//               </div>
              
//               <div className="tnm-row">
//                 <span className="tnm-label">Notice Status:</span>
//                 <span className={`tnm-status ${noticeData.NoticeStatus?.toLowerCase()}`}>
//                   {noticeData.NoticeStatus || 'N/A'}
//                 </span>
//               </div>
              
//               <div className="tnm-row tnm-row-full">
//                 <span className="tnm-label">Notice Reason:</span>
//                 <span className="tnm-value">{noticeData.NoticeReason || 'No reason provided'}</span>
//               </div>
              
//               {noticeData.ApprovedBy && (
//                 <div className="tnm-row">
//                   <span className="tnm-label">Approved By:</span>
//                   <span className="tnm-value">{noticeData.ApprovedBy}</span>
//                 </div>
//               )}
              
//               {noticeData.ApprovedDate && (
//                 <div className="tnm-row">
//                   <span className="tnm-label">Approved Date:</span>
//                   <span className="tnm-value">{formatDate(noticeData.ApprovedDate)}</span>
//                 </div>
//               )}
//             </div>
//           ) : (
//             <div className="tnm-error">No notice details found</div>
//           )}
//         </div>
        
//         <div className="tnm-footer">
//           <button className="tnm-close-footer-btn" onClick={onClose}>
//             Close
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default OwnerTenantNoticeModal;